/* eslint-disable no-unsafe-optional-chaining */
import { Icon } from "@iconify/react";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";
import { useState } from "react";
import AlertDialog from "../../components/shared/AlertDialog";
import { Input } from "../../components/shared/InputField";
import { Select } from "../../components/shared/Select";
import { SwitchButton } from "../../components/shared/SwitchButton";
import Tooltip from "../../components/shared/Tooltip";
import {
  Box,
  ButtonPrimary,
  HStack,
  VStack,
  isEsopViewer,
} from "../../components/utils";
import {
  useBuybackOffers,
  useEditPilotMode,
} from "../../queries/optionBuyback";
import {
  useAdvanceFilterDialog,
  useEditPaymentLinkDialog,
} from "../../store/useDialogStore";
import {
  MonetaryEventState,
  OptionBuyBackDetails,
  RecurringIntervalUnit,
} from "../../types/OptionBuyback";
import {
  formatCurrency,
  getCurrencySymbol,
  getCurrencyType,
} from "../../utils/currencyFormatter";
import { formatDisplayDate } from "../../utils/date";
import useIsMobile from "../../utils/detectDevice";
import StatusLabel from "../esopOverview/StatusLabel";

function BuybackDetails({
  buybackDetail,
}: {
  buybackDetail: OptionBuyBackDetails;
}) {
  const currencySymbol = getCurrencySymbol();
  const isUserEsopViewer = isEsopViewer();
  const currency = getCurrencyType();
  const navigate = useNavigate();
  const { id } = useParams();
  const _id = id ?? "";

  // Actions
  const { state: showAdvanceFilterDialog, setState: setShowAdvanceFilters } =
    useAdvanceFilterDialog();

  const {
    refetch,
    data: _buybackStateData,
    isPlaceholderData,
  } = useBuybackOffers(_id);
  const buybackStateData = _buybackStateData || [];

  function getTotalEligibility() {
    let totalEligibility = 0;
    const isFractional = buybackStateData.some((item) => item.isFractional);
    buybackStateData.forEach((item) => {
      totalEligibility += item.eligibility;
    });
    return isFractional
      ? parseFloat(totalEligibility.toFixed(4))
      : Math.floor(totalEligibility);
  }
  function getTotalVestedShare() {
    let totalVestedShare = 0;
    const isFractional = buybackStateData.some((item) => item.isFractional);
    buybackStateData.forEach((item) => {
      totalVestedShare += item.vested;
    });
    return isFractional
      ? parseFloat(totalVestedShare.toFixed(4))
      : Math.floor(totalVestedShare);
  }
  const [confirmDialog, setConfirmDialog] = useState({ open: false });
  function editDetails() {
    setConfirmDialog({ open: true });
  }

  const { isMobile } = useIsMobile();
  const { mutate: pilotMode, isLoading } = useEditPilotMode();
  function editAutoPilot(id: string | undefined, mode: boolean) {
    const pilotDto: { eventId: string | undefined; mode: boolean } = {
      eventId: id,
      mode,
    };
    // eslint-disable-next-line no-useless-return
    if (!id) return;
    pilotMode(pilotDto, {
      onSuccess: (data) => {
        toast(data, { autoClose: 2000, type: "success" });
      },
      onError: (err) => {
        toast("Something went wrong", { autoClose: 2000, type: "error" });
      },
    });
  }

  const { state: showPaymentLinkDialog, setState: setShowPaymentLinkDialog } =
    useEditPaymentLinkDialog();
  function editLink(link: string) {
    setShowPaymentLinkDialog({ open: true, link, eventId: _id });
  }
  return (
    <VStack className={`${isPlaceholderData ? "loading" : ""} "gap-4 "`}>
      <>
        <Box
          className={`flex ${
            isMobile
              ? "flex-col"
              : "flex-row justify-between gap-12  items-center "
          }  p-4 bg-white border-b border-solid rounded`}
        >
          <Box className="flex items-center gap-12">
            <p
              className={`${
                isMobile ? "text-sm" : "text-lg"
              }  font-medium text-stone-700"`}
            >
              {buybackDetail?.eventName}
            </p>
            <StatusLabel state={buybackDetail?.eventState || ""}></StatusLabel>
          </Box>
          <HStack className="items-center justify-start gap-12 ">
            <div className="flex flex-row-reverse items-center justify-start gap-2">
              <SwitchButton
                className="text-sm font-medium cursor-default text-gray-dark"
                value={buybackDetail?.recurringSetting.isRecurring || false}
                label={"Recurring Buy Back"}
              />
            </div>
            {buybackDetail?.recurringSetting.isRecurring && (
              <div className="flex flex-row-reverse items-center justify-start gap-1">
                {isLoading && (
                  <Icon
                    className="animate-spin text-orange-501"
                    icon="lucide:loader-2"
                    width={24}
                    height={24}
                  />
                )}
                <SwitchButton
                  value={buybackDetail?.isOnAutoPilot}
                  label={"AutoPilot"}
                  className="text-sm font-medium cursor-pointer text-gray-dark"
                  onClick={() => {
                    if (!isUserEsopViewer)
                      editAutoPilot(
                        buybackDetail?.id,
                        !buybackDetail?.isOnAutoPilot
                      );
                  }}
                />

                <Tooltip
                  text={`Auto Pilot mode 'ON' will not require manual actions for start/close of a liquidity event. `}
                  _className="w-[400px]"
                >
                  <Icon
                    icon="material-symbols:info-outline-rounded"
                    height={24}
                    width={24}
                    className="w-full text-xs font-medium rounded cursor-pointer text-slate-dark"
                  />
                </Tooltip>
              </div>
            )}
          </HStack>
        </Box>
        {buybackDetail && (
          <VStack
            className={`${
              isMobile ? "p-2" : "p-6"
            } gap-4 bg-[#fefefe] rounded m drop-shadow-md`}
          >
            <VStack className="gap-8">
              <Box
                className={` flex ${
                  isMobile
                    ? "flex-col gap-8"
                    : "flex-row  justify-between gap-12"
                } `}
              >
                <div
                  className={` flex flex-row items-center flex-1 gap-4 ${
                    isMobile ? "justify-between " : " justify-start "
                  } `}
                >
                  <div
                    className={`${
                      isMobile ? "whitespace-nowrap" : ""
                    } w-full text-sm text-gray-600 flex-nowrap whitespace-nowrap flex-1`}
                  >
                    <p>Option Buy Back name</p>
                  </div>
                  <div
                    className={`${
                      isMobile ? "text-end" : " "
                    } text-black bg-white flex-1 `}
                  >
                    <p>{buybackDetail?.eventName}</p>
                  </div>
                </div>
                <div className="flex flex-row items-center justify-start flex-1 gap-4">
                  {(buybackDetail?.eventPercentage || 0) > 0 ? (
                    <>
                      <div
                        className={`${
                          isMobile ? "whitespace-nowrap" : ""
                        } w-full flex-1 text-sm text-gray-600 flex-nowrap whitespace-nowrap`}
                      >
                        <p>Percentage (%)</p>
                      </div>
                      <div
                        className={`${
                          isMobile ? "text-end" : " "
                        } text-black bg-white flex-1 `}
                      >
                        <p>{(buybackDetail?.eventPercentage || 0) * 100}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div
                        className={`${
                          isMobile ? "whitespace-nowrap" : ""
                        } w-full flex-1 text-sm text-gray-600 flex-nowrap whitespace-nowrap`}
                      >
                        <p>Offer uploaded through excel</p>
                      </div>
                      <div
                        className={`${
                          isMobile ? "text-end" : " "
                        } text-black bg-white flex-1 `}
                      >
                        <p>Yes</p>
                      </div>
                    </>
                  )}
                </div>
              </Box>
              <Box
                className={` flex ${
                  isMobile
                    ? " flex-col gap-8"
                    : "flex-row  justify-start gap-12"
                } `}
              >
                <div className="flex flex-row items-center justify-between flex-1 gap-4">
                  <div
                    className={`${
                      isMobile ? "whitespace-nowrap" : ""
                    } w-full flex-1 text-sm text-gray-600 flex-nowrap whitespace-nowrap`}
                  >
                    {" "}
                    <p>Buy Back price</p>
                  </div>
                  <div
                    className={`${
                      isMobile ? "text-end" : " "
                    } text-black bg-white flex-1 `}
                  >
                    <p>
                      {formatCurrency(
                        buybackDetail?.monetaryDetails?.price || 0,
                        currency
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex flex-row items-center justify-between flex-1 gap-4">
                  <div
                    className={`${
                      isMobile ? "whitespace-nowrap" : ""
                    } w-full flex-1 text-sm text-gray-600 flex-nowrap whitespace-nowrap`}
                  >
                    {" "}
                    <p>Buy Back start date</p>
                  </div>
                  <div
                    className={`${
                      isMobile ? "text-end" : " "
                    } text-black bg-white flex-1 `}
                  >
                    <p>
                      {formatDisplayDate(
                        buybackDetail?.startDate || "1979-01-01"
                      )}
                    </p>
                  </div>
                </div>
              </Box>
              <Box
                className={` flex ${
                  isMobile
                    ? " flex-col gap-8"
                    : "flex-row  justify-start gap-12"
                } `}
              >
                <div className="flex flex-row items-center justify-between flex-1 gap-4">
                  <div
                    className={`${
                      isMobile ? "whitespace-nowrap" : ""
                    } w-full flex-1 text-sm text-gray-600 flex-nowrap whitespace-nowrap`}
                  >
                    {" "}
                    <p>Estimated value</p>
                  </div>
                  <div
                    className={`${
                      isMobile ? "text-end" : " "
                    } text-black bg-white flex-1 `}
                  >
                    <p>
                      {formatCurrency(
                        getTotalEligibility() *
                          (buybackDetail?.monetaryDetails.price || 0),
                        currency
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex flex-row items-center justify-between flex-1 gap-4">
                  <div
                    className={`${
                      isMobile ? "whitespace-nowrap" : ""
                    } w-full flex-1 text-sm text-gray-600 flex-nowrap whitespace-nowrap`}
                  >
                    {" "}
                    <p>Effective Buy Back date</p>
                  </div>
                  <div
                    className={`${
                      isMobile ? "text-end" : " "
                    } text-black bg-white flex-1 `}
                  >
                    <p>
                      {formatDisplayDate(
                        buybackDetail?.vestingCutOffDate || "1979-01-01"
                      )}
                    </p>
                  </div>
                </div>
              </Box>
              <Box
                className={` flex ${
                  isMobile
                    ? " flex-col gap-8"
                    : "flex-row  justify-start gap-12"
                } `}
              >
                <div className="flex flex-row items-center justify-between flex-1 gap-4">
                  <div className="flex-1 w-full text-sm text-gray-600 flex-nowrap">
                    <p>Vested options</p>
                  </div>
                  <div
                    className={`${
                      isMobile ? "text-end" : " "
                    } text-black bg-white flex-1 `}
                  >
                    <p>{getTotalVestedShare().toLocaleString(currency)}</p>
                  </div>
                </div>
                <div className="flex flex-row items-center justify-start flex-1 gap-4">
                  <div className="flex-1 w-full text-sm text-gray-600 flex-nowrap">
                    <p>Buy-back offer end date</p>
                  </div>
                  <div
                    className={`${
                      isMobile ? "text-end" : " "
                    } text-black bg-white flex-1 `}
                  >
                    <p>
                      {formatDisplayDate(
                        buybackDetail?.endDate || "1979-01-01"
                      )}
                    </p>
                  </div>
                </div>
              </Box>
              <Box
                className={` flex ${
                  isMobile
                    ? " flex-col gap-8"
                    : "flex-row  justify-start gap-12"
                } `}
              >
                <div className="flex flex-row items-center justify-between flex-1 gap-4">
                  <div className="flex-1 w-full text-sm text-gray-600 flex-nowrap">
                    {" "}
                    <p> Eligible options</p>
                  </div>
                  <div
                    className={`${
                      isMobile ? "text-end" : " "
                    } text-black bg-white flex-1 `}
                  >
                    <p>{getTotalEligibility().toLocaleString(currency)}</p>
                  </div>
                </div>
                <div className="flex flex-row items-center justify-between flex-1 gap-4">
                  <div className="flex-1 w-full text-sm text-gray-600 flex-nowrap">
                    <p>Description</p>
                  </div>
                  <div
                    className={`${
                      isMobile ? "text-end" : " "
                    } text-black bg-white flex-1 `}
                  >
                    <p>{buybackDetail?.description}</p>
                  </div>
                </div>
              </Box>
              <Box className="flex flex-col gap-8">
                <Box
                  className={`flex ${
                    isMobile ? "flex-col" : "flex-row"
                  } gap-12`}
                >
                  {buybackDetail?.monetaryDetails?.paymentLink && (
                    <div className="flex flex-row items-center flex-1 gap-2">
                      <div className="w-1/2 text-sm text-gray-600 whitespace-nowrap">
                        <p>Payment Link</p>
                      </div>
                      <div className="flex-1 pl-1 text-black bg-white">
                        <div className="flex flex-row items-center gap-4">
                          <a
                            href={buybackDetail?.monetaryDetails?.paymentLink}
                            className="underline text-orange-501"
                          >
                            {`${buybackDetail?.monetaryDetails?.paymentLink.slice(
                              0,
                              10
                            )}...`}
                          </a>
                          <p
                            onClick={() =>
                              editLink(
                                buybackDetail?.monetaryDetails?.paymentLink
                              )
                            }
                            className="cursor-pointer"
                          >
                            <Icon
                              icon="lets-icons:edit-duotone"
                              className="w-6 h-6"
                            />
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  {buybackDetail?.recurringSetting.isRecurring ? (
                    <div className="flex flex-row items-center flex-1 gap-3">
                      <div className="w-1/2 text-sm text-gray-600 whitespace-nowrap">
                        <p>Recurring Interval</p>
                      </div>
                      <HStack
                        className={`max-w-min items-center rounded bg-slate-light text-slate-dark`}
                      >
                        <Input
                          type="number"
                          placeholder="Enter Interval"
                          disabled
                          className="w-20"
                          value={
                            buybackDetail?.recurringSetting.recurringInterval
                          }
                        />
                        <Select
                          options={[
                            RecurringIntervalUnit.QUARTER,
                            RecurringIntervalUnit.MONTH,
                            RecurringIntervalUnit.YEAR,
                          ]}
                          className="w-32"
                          disabled
                          value={
                            buybackDetail?.recurringSetting
                              .recurringIntervalUnit
                          }
                        />
                      </HStack>
                    </div>
                  ) : null}
                  {(!buybackDetail?.monetaryDetails?.paymentLink ||
                    !buybackDetail?.recurringSetting.isRecurring) && (
                    <div className="flex flex-row items-center justify-between flex-1 gap-4">
                      <div className="flex-1 w-full text-sm text-gray-600 flex-nowrap">
                        <p>Deduct Strike Price</p>
                      </div>
                      <div
                        className={`${
                          isMobile ? "text-end" : " "
                        } text-black bg-white flex-1`}
                      >
                        <p>{`${
                          buybackDetail?.monetaryDetails?.includeStrikePrice
                            ? "Yes"
                            : "No"
                        }`}</p>
                      </div>
                    </div>
                  )}
                  {!buybackDetail?.monetaryDetails?.paymentLink &&
                    !buybackDetail?.recurringSetting.isRecurring && (
                      <div className="flex-1" />
                    )}
                </Box>
                {buybackDetail?.monetaryDetails?.paymentLink &&
                  buybackDetail?.recurringSetting.isRecurring && (
                    <Box
                      className={`flex ${
                        isMobile ? "flex-col" : "flex-row"
                      } gap-8`}
                    >
                      <div className="flex flex-row items-center justify-between flex-1 gap-4">
                        <div className="flex-1 w-full text-sm text-gray-600 flex-nowrap">
                          <p>Deduct Strike Price</p>
                        </div>
                        <div
                          className={`${
                            isMobile ? "text-end" : " "
                          } text-black bg-white flex-1`}
                        >
                          <p>{`${
                            buybackDetail?.monetaryDetails?.includeStrikePrice
                              ? "Yes"
                              : "No"
                          }`}</p>
                        </div>
                      </div>
                      <div className="flex-1" />
                    </Box>
                  )}
              </Box>

              <p
                onClick={() => setShowAdvanceFilters({ open: true, id: _id })}
                className="p-2 px-5 py-2 font-normal border border-dashed rounded cursor-pointer max-w-min whitespace-nowrap h-11 text-sm1 focus:opacity-90 hover:bg-slate-100 border-orange-501"
              >
                Show Advance Filter
              </p>
            </VStack>
            <AlertDialog
              onClose={() => setConfirmDialog({ open: false })}
              open={confirmDialog.open}
              message="Any edits will require you to regenerate or re-upload the offer list. Are you sure you wish to continue?"
              onPrimaryAction={() => {
                setConfirmDialog({ open: false });
                navigate(`/options/edit-options-buyback/${_id}?mode=edit`);
              }}
              onSecondaryAction={() => setConfirmDialog({ open: false })}
            />
            <HStack className="flex-row-reverse self-end">
              {buybackDetail?.eventState === MonetaryEventState.DRAFT &&
              !isUserEsopViewer ? (
                <ButtonPrimary
                  className="flex items-center self-end justify-center px-8 flex-nowrap"
                  onClick={editDetails}
                >
                  Edit Details
                </ButtonPrimary>
              ) : (
                ""
              )}
            </HStack>
          </VStack>
        )}
      </>
    </VStack>
  );
}

export default BuybackDetails;
